<template>
  <div class="relative bg-black overflow-hidden text-white" style="height: 100dvh">
    <loading v-if='loading || publishing' />

    <pbc-error v-if='!!error' :error='error' :retry='true' @retry='onRetry' message='Could not load template.' />

    <template v-if='!loading && !error && slots.length > 0'>

      <!-- Full-screen game preview in background -->
      <div class="absolute inset-0">
        <runner-game
          v-if='liveGamedata'
          :key='previewKey'
          :gamedata='liveGamedata'
          :start-pixi='true'
          :game-end-controls='false'
          :enable-error-handler='false'
          class="w-full h-full"
        />
        <img v-else-if='thumbnailUrl' :src='thumbnailUrl' class="w-full h-full object-cover" />
      </div>

      <!-- Bottom sheet -->
      <div
        class="absolute left-0 right-0 bg-gray-900 rounded-t-3xl flex flex-col"
        :style="{ bottom: keyboardOffset + 'px', maxHeight: '60dvh' }"
      >
        <!-- Drag handle -->
        <div class="flex justify-center pt-3 pb-2 flex-shrink-0">
          <div class="w-10 h-1 bg-gray-600 rounded-full" />
        </div>

        <!-- Title -->
        <h1 class="text-base font-bold text-center px-4 pb-3 flex-shrink-0">{{ templateTitle }}</h1>

        <!-- Scrollable inputs -->
        <div class="overflow-y-auto flex-1 px-4 flex flex-col gap-3 pb-3">

          <div v-for='slot in slots' :key='slot.id' class="flex flex-col gap-1">
            <label class="text-sm font-semibold text-gray-300">
              {{ slot.label }}<span v-if='slot.required' class="text-red-400 ml-1">*</span>
            </label>
            <textarea
              v-model='slotValues[slot.id]'
              :placeholder='slot.hint || slot.label'
              rows='2'
              class="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white text-base resize-none focus:outline-none focus:border-blue-500"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-sm font-semibold text-gray-300">Title</label>
            <input
              v-model='postTitle'
              type='text'
              placeholder='Give your post a title...'
              class="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white text-base focus:outline-none focus:border-blue-500"
            />
          </div>

          <p v-if='publishError' class="text-red-400 text-sm text-center">{{ publishError }}</p>

        </div>

        <!-- Action buttons — always visible at sheet bottom -->
        <div class="flex-shrink-0 flex gap-3 px-4 py-3 border-t border-gray-800">
          <button
            @click='openInEditor'
            class="flex-1 py-3 rounded-xl bg-gray-700 text-gray-200 font-semibold text-base"
          >
            Edit
          </button>
          <button
            @click='publish'
            :disabled='publishing || !canPublish'
            class="flex-2 px-6 py-3 rounded-xl font-semibold text-base"
            :class='canPublish && !publishing ? "bg-blue-600 text-white" : "bg-gray-600 text-gray-400 cursor-not-allowed"'
          >
            <span v-if='publishing'>Publishing…</span>
            <span v-else>Publish →</span>
          </button>
        </div>

      </div>

    </template>

    <!-- No slots: redirect immediately to editor (existing behaviour) -->
    <pbc-message ref="message" />
  </div>
</template>

<script>
import { savePostDraftQuery, getTemplateQuery } from '@/gql/Queries.js';
import { format, formatISO9075, addMinutes } from 'date-fns';
import gql from 'graphql-tag';
import PbcMessage from '@ui/molecules/PbcMessage.vue';
import { debounce as _debounce } from 'lodash';
import ComponentLazyLoader from '@/lazyloader.js';
const { fn: RunnerGame } = ComponentLazyLoader(() => import(/* webpackChunkName: "play" */ '@/play/components/RunnerGame.vue'));

const LONG_PATTERN = "yyyy-MM-dd'T'HH:mm:ss";

export default {
  inject: ['api'],

  data() {
    return {
      loading: true,
      error: '',
      templateTitle: '',
      thumbnailUrl: '',
      slots: [],
      slotValues: {},
      postTitle: '',
      rawGamedata: null,
      publishing: false,
      publishError: '',
      previewKey: 0,
      keyboardOffset: 0,
    };
  },

  mounted() {
    if (window.visualViewport) {
      this._onViewportResize = () => {
        // Keyboard height = space between bottom of visual viewport and bottom of window
        const keyboardHeight = window.innerHeight - window.visualViewport.height - window.visualViewport.offsetTop;
        this.keyboardOffset = Math.max(0, keyboardHeight);
      };
      window.visualViewport.addEventListener('resize', this._onViewportResize);
    }
  },

  beforeDestroy() {
    if (window.visualViewport && this._onViewportResize) {
      window.visualViewport.removeEventListener('resize', this._onViewportResize);
    }
  },

  apollo: {
    getTemplate: {
      query: getTemplateQuery,
      variables() {
        return { id: this.templateId };
      },
      error(error) {
        this.error = error.message;
        this.loading = false;
      },
      async result({ data, loading }) {
        if (loading || !data?.getTemplate) return;

        const t = data.getTemplate;
        const gamedata = typeof t.data === 'string' ? JSON.parse(t.data) : t.data;

        this.templateTitle = t.title;
        this.thumbnailUrl = t.thumbnailImage ? this.api.makeImageUrlEditor(t.thumbnailImage) : '';
        this.rawGamedata = gamedata;
        this.postTitle = t.title;

        const templateComponents = gamedata?.template?.components || [];
        this.slots = templateComponents
          .filter(c => c.slot)
          .map(c => ({
            id:        c.slot.id,
            label:     c.slot.label,
            hint:      c.slot.hint || '',
            type:      c.slot.type || 'text',
            required:  c.slot.required !== false,
            component: c.component,
          }));

        const values = {};
        this.slots.forEach(s => { values[s.id] = ''; });
        this.slotValues = values;

        // Always open in the editor — slot labels are shown natively in the editor toolbar
        await this.saveAndOpenEditor(gamedata, t.title);
      },
    },
  },

  computed: {
    templateId() {
      return this.$route.params.templateId;
    },
    liveGamedata() {
      if (!this.rawGamedata) return null;
      return this.applySlots(this.rawGamedata);
    },
    canPublish() {
      return (
        this.postTitle.trim().length > 0 &&
        this.slots.filter(s => s.required).every(s => (this.slotValues[s.id] || '').trim().length > 0)
      );
    },
  },

  watch: {
    slotValues: {
      deep: true,
      handler: _debounce(function() { this.previewKey++; }, 600),
    },
  },

  methods: {
    onRetry() {
      this.$apollo.queries.getTemplate.refetch();
      this.error = '';
      this.loading = true;
    },

    applySlots(gamedata) {
      const cloned = JSON.parse(JSON.stringify(gamedata));
      const componentToSlot = {};
      for (const comp of (cloned.template?.components || [])) {
        if (comp.slot) componentToSlot[comp.component] = comp.slot.id;
      }
      for (const scene of (cloned.scenes || [])) {
        for (const comp of (scene.components || [])) {
          const slotId = componentToSlot[comp.id];
          if (slotId && slotId in this.slotValues) {
            if (comp.type === 'text') comp.text = this.slotValues[slotId];
          }
        }
      }
      return cloned;
    },

    async publish() {
      if (!this.canPublish || this.publishing) return;
      this.publishing = true;
      this.publishError = '';

      try {
        const gamedata = this.applySlots(this.rawGamedata);

        const signedResp = await this.$apollo.mutate({
          fetchPolicy: 'no-cache',
          mutation: gql`mutation { getSignedGameUploadUrl { id url } }`,
        });
        const { id: gameId, url: signedUrl } = signedResp.data.getSignedGameUploadUrl;
        gamedata.id = gameId;

        await fetch(signedUrl, {
          method: 'PUT',
          body: JSON.stringify(gamedata),
          headers: { 'Content-Type': 'application/json' },
        });

        const postResp = await this.$apollo.mutate({
          mutation: gql`
            mutation createPost($parent: String!, $op: String!, $title: String!, $game: String!) {
              createPost(parent: $parent, op: $op, title: $title, game: $game) {
                id title game author { id username }
              }
            }
          `,
          variables: { parent: 'root', op: 'root', title: this.postTitle, game: gameId },
        });

        const post = postResp.data.createPost;
        this.$ana.track({ eventCategory: 'template', eventAction: 'publishFromSlots', extra: { templateId: this.templateId, postId: post.id } });

        this.$refs.message.show({
          title: 'Your post has been created.',
          onClose: () => this.$router.replace({ name: 'new' }),
        });

      } catch (err) {
        this.publishError = err.message || 'Something went wrong. Please try again.';
        this.publishing = false;
      }
    },

    async openInEditor() {
      const gamedata = Object.values(this.slotValues).some(v => v.trim().length > 0)
        ? this.applySlots(this.rawGamedata)
        : this.rawGamedata;
      await this.saveAndOpenEditor(gamedata, this.postTitle);
    },

    async saveAndOpenEditor(gamedata, title) {
      const created = format(this.getUTCDate(), LONG_PATTERN);
      const updated = format(this.getUTCDate(), LONG_PATTERN);
      const draftId = this.$route.params.draftId || formatISO9075(this.getUTCDate(), { format: 'basic' }).replace(' ', '-');

      await this.$apollo.mutate({
        mutation: savePostDraftQuery,
        fetchPolicy: 'no-cache',
        client: 'localClient',
        variables: { id: draftId, created, updated, title, gamedata: JSON.stringify(gamedata), parentId: 'root', opId: 'root' },
      });

      this.$router.replace({ name: 'create', params: { type: 'post', draftId } });
    },

    getUTCDate() {
      const date = new Date();
      return addMinutes(date, date.getTimezoneOffset());
    },
  },

  components: {
    PbcMessage,
    RunnerGame,
  },
};
</script>
